import { Link } from 'react-router';
import type { LineageEdge, LineageLot, LineageTree } from '../api/types.js';
import { ORIGIN_LABELS } from '../lib/format.js';
import { Badge } from './badge.js';

type Direction = 'upstream' | 'downstream';

interface Branch {
  readonly lot: LineageLot;
  readonly quantityConsumed: number;
}

function branchesOf(lotId: string, tree: LineageTree, direction: Direction): Branch[] {
  const lotsById = new Map(tree.lots.map((lot) => [lot.lotId, lot]));
  const linked = (edge: LineageEdge) =>
    (direction === 'upstream' ? edge.childLotId : edge.parentLotId) === lotId;
  const other = (edge: LineageEdge) =>
    direction === 'upstream' ? edge.parentLotId : edge.childLotId;
  return tree.edges
    .filter(linked)
    .flatMap((edge) => {
      const lot = lotsById.get(other(edge));
      return lot === undefined ? [] : [{ lot, quantityConsumed: edge.quantityConsumed }];
    })
    .sort((left, right) => left.lot.sku.localeCompare(right.lot.sku));
}

function originTone(lot: LineageLot) {
  return lot.origin === 'FARMA_CENTRAL'
    ? 'accent'
    : lot.origin === 'OWN_PRODUCTION'
      ? 'success'
      : 'neutral';
}

function Branches({
  lotId,
  tree,
  direction,
  visited,
}: {
  lotId: string;
  tree: LineageTree;
  direction: Direction;
  visited: ReadonlySet<string>;
}) {
  const branches = branchesOf(lotId, tree, direction).filter(
    (branch) => !visited.has(branch.lot.lotId),
  );
  if (branches.length === 0) return null;
  return (
    <ul className="ml-4 border-l border-border pl-4">
      {branches.map((branch) => (
        <li key={branch.lot.lotId} className="py-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/trazabilidad?lote=${encodeURIComponent(branch.lot.lotId)}`}
              className="font-medium break-all text-accent underline"
            >
              {branch.lot.lotId}
            </Link>
            <span className="text-sm text-muted-foreground">
              {branch.lot.sku}
              {branch.lot.productName !== null && ` · ${branch.lot.productName}`}
            </span>
            <Badge tone={originTone(branch.lot)}>
              {ORIGIN_LABELS[branch.lot.origin] ?? branch.lot.origin}
            </Badge>
            <span className="text-sm">
              {direction === 'upstream' ? 'aportó' : 'consumió'}{' '}
              <strong>{branch.quantityConsumed}</strong> u
            </span>
          </div>
          <Branches
            lotId={branch.lot.lotId}
            tree={tree}
            direction={direction}
            visited={new Set([...visited, branch.lot.lotId])}
          />
        </li>
      ))}
    </ul>
  );
}

export function LineageTreeView({
  rootLotId,
  tree,
  direction,
  emptyMessage,
}: {
  rootLotId: string;
  tree: LineageTree;
  direction: Direction;
  emptyMessage: string;
}) {
  if (tree.edges.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  }
  return (
    <Branches lotId={rootLotId} tree={tree} direction={direction} visited={new Set([rootLotId])} />
  );
}
